import isEqual from 'react-fast-compare';

import {
  areTypedValuesEquivalent,
  coerceToPrimitive,
  flattenValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {extractComparableMinMaxValue} from 'procedures/utils/procedureParameterMinMax';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {
  compareValueToRange,
  isComparableTypedValue,
  isOutOfRange,
  type MinMaxTypedValue,
} from './PMEIParameterRange';

/**
 * For float and datetime parameters, "matches the setpoint" must be a numeric comparison, using
 * the same normalization (parseTextAsFloat / epoch millis via extractComparableMinMaxValue) that
 * the min/max range check uses — otherwise a value like `37.0` would be graded as differing from a
 * setpoint of `37`, contradicting the range check that treats them as equal. Text, selector, and
 * entity values keep areTypedValuesEquivalent, where representation/id equality is the correct
 * semantic and numeric coercion would be wrong.
 */
function areValuesEquivalentForGrading(a: TypedValue, b: TypedValue): boolean {
  const numericA = numericValueForMatch(a);
  const numericB = numericValueForMatch(b);
  if (numericA != null && numericB != null) {
    return numericA === numericB;
  }
  return areTypedValuesEquivalent(a, b);
}

/** Comparable number for float/datetime values; null for other types or unparseable values. */
function numericValueForMatch(value: TypedValue): number | null {
  if (value.type === FieldType.FLOAT || value.type === FieldType.DATETIME) {
    return extractComparableMinMaxValue(value);
  }
  return null;
}

export enum CellColorGrade {
  NONE = 'none', // no background — empty cell, or >1 setpoint so grading is skipped
  UNPLANNED = 'unplanned', // rule 1 → gray (#F0F1F2)
  MISMATCH = 'mismatch', // rule 2 → yellow (#FCF8D6)
  OUT_OF_RANGE = 'outOfRange', // rule 3 → orange (#FFD8A8); takes precedence over MISMATCH
  MATCH = 'match', // rule 4 → no background override
}

/**
 * Computes the color grade for a data-parameter cell (Confirmed, Measured) based on how its values
 * relate to the reference (setpoint) values and the optional numeric/datetime range.
 *
 * @param cellValues - The recorded values for the cell (may include empty/null entries).
 * @param referenceValues - The planned (setpoint) values for the parameter.
 * @param minimum - Optional lower bound; null/undefined means unbounded.
 * @param maximum - Optional upper bound; null/undefined means unbounded.
 */
export function computeDataParameterCellColorGrade(
  cellValues: Array<TypedValue | null>,
  referenceValues: Array<TypedValue | null>,
  minimum: MinMaxTypedValue = null,
  maximum: MinMaxTypedValue = null
): CellColorGrade {
  return gradeDimension(
    cellValues,
    referenceValues,
    areValuesEquivalentForGrading,
    (v) => isComparableTypedValue(v) && isOutOfRange(compareValueToRange(v, minimum, maximum))
  );
}

/**
 * The shared grading ladder for a single dimension. Grades are checked in order:
 * NONE (no cell values, or more than one reference) → UNPLANNED (no reference) →
 * OUT_OF_RANGE → MISMATCH → MATCH.
 *
 * Callers customize two steps by injecting predicates:
 * - `areEquivalent` decides MATCH vs. MISMATCH.
 * - `isCellOutOfRange` decides OUT_OF_RANGE; when omitted, that grade is never produced.
 *
 * Data parameters supply a range predicate, while material-input dimensions (entity, amount)
 * supply their own equality and omit the range predicate.
 */
function gradeDimension(
  cellValues: Array<TypedValue | null>,
  referenceValues: Array<TypedValue | null>,
  areEquivalent: (a: TypedValue, b: TypedValue) => boolean,
  isCellOutOfRange?: (v: TypedValue) => boolean
): CellColorGrade {
  const nonEmptyCell = cellValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));
  if (nonEmptyCell.length === 0) {
    return CellColorGrade.NONE;
  }

  const nonEmptyRef = referenceValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));

  if (nonEmptyRef.length > 1) {
    // A dimension has at most one planned value in practice; with no single target to compare
    // against there is nothing meaningful to grade, so skip.
    return CellColorGrade.NONE;
  }

  if (nonEmptyRef.length === 0) {
    return CellColorGrade.UNPLANNED;
  }

  const reference = nonEmptyRef[0];

  if (isCellOutOfRange != null && nonEmptyCell.some((v) => isCellOutOfRange(v))) {
    return CellColorGrade.OUT_OF_RANGE;
  }

  if (nonEmptyCell.every((v) => areEquivalent(v, reference))) {
    return CellColorGrade.MATCH;
  }

  return CellColorGrade.MISMATCH;
}

/**
 * Entity-link equality by underlying ID rather than display name. Two links pointing at different
 * entities that happen to share a display name must grade as a mismatch.
 */
function areEntityEquivalentById(a: TypedValue, b: TypedValue): boolean {
  return isEqual(
    coerceToPrimitive(flattenValue(a), {shouldUseIds: true}),
    coerceToPrimitive(flattenValue(b), {shouldUseIds: true})
  );
}

/**
 * Predicate for the entity-material amount dimension: entity amount differences are deliberately
 * not flagged, so we treat every confirmed amount as matching its planned amount. This still lets
 * `gradeDimension` surface UNPLANNED (planned amount absent, confirmed present) while never
 * producing MISMATCH.
 */
const amountDifferencesNotGraded = (): boolean => true;

/**
 * Worst-case aggregation of two dimension grades: yellow (MISMATCH) beats gray (UNPLANNED) beats
 * matched/none. Mirrors the worst-case multi-reading aggregation `gradeDimension` applies within a
 * single dimension.
 */
function combineGrades(a: CellColorGrade, b: CellColorGrade): CellColorGrade {
  if (a === CellColorGrade.MISMATCH || b === CellColorGrade.MISMATCH) {
    return CellColorGrade.MISMATCH;
  }
  if (a === CellColorGrade.UNPLANNED || b === CellColorGrade.UNPLANNED) {
    return CellColorGrade.UNPLANNED;
  }
  if (a === CellColorGrade.MATCH || b === CellColorGrade.MATCH) {
    return CellColorGrade.MATCH;
  }
  return CellColorGrade.NONE;
}

/**
 * Color grade for a material-input Confirmed cell. A material-input value has two dimensions — the
 * material (an entity link or free text) and its optional amount — each graded independently and
 * then combined worst-case (MISMATCH beats UNPLANNED beats MATCH/NONE, via `combineGrades`).
 *
 * The rules differ by material type:
 *
 * Entity materials (`isEntity` true) — grade the entity fully but the amount only for the unplanned
 * case:
 *   - MISMATCH: confirmed entity differs from planned entity, compared by entity ID (not display
 *     name) so two distinct entities sharing a name still flag.
 *   - UNPLANNED: planned entity absent while a confirmed entity is present.
 *   - UNPLANNED: planned amount absent while a confirmed amount is present.
 *   - A differing entity amount is NOT flagged — the amount is graded with an always-equal
 *     predicate, so it can only ever surface as gray (unplanned) or matched, never yellow.
 *
 * Text materials (`isEntity` false) — grade both dimensions with real equality:
 *   - MISMATCH: confirmed text value differs from planned, or confirmed amount differs from
 *     planned amount.
 *   - UNPLANNED: planned text value absent, or planned amount absent, while the confirmed
 *     counterpart is present.
 *
 * In both cases the cell is left ungraded (NONE/MATCH) when everything matches or the confirmed
 * cell is empty.
 */
export interface MaterialInputGradingValue {
  material: TypedValue;
  amount: TypedValue | null;
}

export function computeMaterialInputCellColorGrade(
  confirmedValues: Array<MaterialInputGradingValue>,
  plannedValues: Array<MaterialInputGradingValue>,
  isEntity: boolean
): CellColorGrade {
  const materialGrade = gradeDimension(
    confirmedValues.map((v) => v.material),
    plannedValues.map((v) => v.material),
    isEntity ? areEntityEquivalentById : areTypedValuesEquivalent
  );
  const amountGrade = gradeDimension(
    confirmedValues.map((v) => v.amount),
    plannedValues.map((v) => v.amount),
    isEntity ? amountDifferencesNotGraded : areTypedValuesEquivalent
  );
  return combineGrades(materialGrade, amountGrade);
}
